/**
 * **Struktur menu: departemen → kategori** — per BRAND.
 *
 * ⭐ Dipisah dari layar Produk (Imron, 2026-08-02): keduanya dikerjakan orang berbeda pada waktu
 * berbeda. Struktur ditata sekali saat onboarding dan jarang disentuh lagi; produk & harga
 * disunting tiap minggu. Satu halaman berisi keduanya memaksa yang sering dipakai melewati yang
 * jarang diubah — dan halaman yang penuh membuat orang berhenti membacanya.
 *
 * ### ⭐⭐ Bentuknya mengikuti master user/role OSB Nexus (Imron, 2026-08-03)
 * Dua kolom daftar + formulir sebaris yang dipakai sebelumnya **tak punya jalan menyunting sama
 * sekali**: salah ketik nama departemen hanya bisa diperbaiki lewat SQL. Bentuk Nexus — tabel yang
 * barisnya diklik lalu laci terbuka — menjawab itu tanpa menambah layar baru, dan sudah dihafal.
 *
 * ⛔ Tak ada penampung "LAINNYA" yang lahir sendiri: departemen dibuat lebih dulu, sadar, baru
 * kategorinya. Tab **Kategori** menguncinya secara harfiah — tanpa departemen, tombol "+ Kategori"
 * mati dan sebabnya tertulis.
 */
import { Alert, Form, Input, InputNumber, Select, Space, Switch, Tabs, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '../api/client';
import type { Category, Department } from '../api/client';
import { canWrite } from '../api/session';
import { BrandPicker } from '../components/BrandPicker';
import { CrudPanel } from '../components/CrudPanel';

/** Kode master: huruf besar, tanpa spasi — dipakai di struk, laporan, dan impor CSV. */
const ATURAN_KODE = { required: true, pattern: /^[A-Za-z0-9_-]+$/ } as const;

/** Kolom status yang sama untuk kedua tabel — "nonaktif" tak boleh cuma terlihat sebagai baris pucat. */
function kolomStatus<T extends { is_active: boolean }>(aktif: string, mati: string): ColumnsType<T>[number] {
  return {
    title: '',
    dataIndex: 'is_active',
    width: 96,
    render: (v: boolean) => (v ? <Tag color="green">{aktif}</Tag> : <Tag>{mati}</Tag>),
  };
}

export function Structure() {
  const { t } = useTranslation();
  const boleh = canWrite();
  const [brandId, setBrandId] = useState<string | null>(null);
  const [department, setDepartment] = useState<readonly Department[]>([]);

  /*
   * ⚠️ Daftar departemen dimuat DI SINI, bukan hanya di dalam panelnya: tab Kategori butuh nama
   * departemen untuk kolomnya DAN untuk pilihan di formulirnya. Dua panel yang masing-masing
   * memuat sendiri akan menampilkan dua versi daftar yang sama sesudah satu penyuntingan.
   */
  const muatDep = useCallback(async (): Promise<readonly Department[]> => {
    if (brandId === null) return [];
    /* ⛔ `includeInactive`: layar INI yang mengelola master, jadi yang nonaktif harus tetap
       terlihat — kalau tidak, mematikan sesuatu berarti kehilangan jalan menyalakannya lagi. */
    const d = await api.departments(brandId, true);
    setDepartment(d);
    return d;
  }, [brandId]);
  useEffect(() => { void muatDep().catch(() => undefined); }, [muatDep]);

  const namaDep = useMemo(
    () => new Map(department.map((d) => [d.id, `${d.code} · ${d.name}`])),
    [department],
  );

  /*
   * ⚠️ Kategori diambil TANPA saringan lalu disaring ke departemen brand ini. Menyaring per
   * departemen berarti satu permintaan per departemen — dan halaman yang menembak sepuluh
   * permintaan saat dibuka terasa rusak jauh sebelum ia benar-benar lambat.
   */
  const muatKat = useCallback(async (): Promise<readonly Category[]> => {
    if (brandId === null) return [];
    const semua = await api.categories(undefined, true);
    const milikBrand = new Set(department.map((d) => d.id));
    return semua.filter((c) => milikBrand.has(c.department_id));
  }, [brandId, department]);

  const kolomDep: ColumnsType<Department> = [
    {
      title: t('master.code'),
      dataIndex: 'code',
      width: 160,
      render: (v: string) => <span data-testid={`dep-${v}`}>{v}</span>,
    },
    { title: t('master.name'), dataIndex: 'name' },
    { title: t('structure.sortOrder'), dataIndex: 'sort_order', width: 90 },
    kolomStatus<Department>(t('outlets.active'), t('outlets.inactive')),
  ];

  const kolomKat: ColumnsType<Category> = [
    {
      title: t('master.code'),
      dataIndex: 'code',
      width: 160,
      render: (v: string) => <span data-testid={`kat-${v}`}>{v}</span>,
    },
    { title: t('master.name'), dataIndex: 'name' },
    {
      /* ⭐ Departemen jadi KOLOM, bukan konteks yang harus diingat dari pilihan di layar sebelah. */
      title: t('master.departments'),
      dataIndex: 'department_id',
      render: (v: string) => namaDep.get(v) ?? '—',
    },
    { title: t('structure.sortOrder'), dataIndex: 'sort_order', width: 90 },
    kolomStatus<Category>(t('outlets.active'), t('outlets.inactive')),
  ];

  /** Field bersama kedua formulir: kode (hanya saat baru), nama, urutan, aktif (hanya saat sunting). */
  const fieldDasar = (editing: { id: string } | null) => (
    <>
      {editing === null ? (
        <Form.Item
          name="code"
          label={t('master.code')}
          rules={[{ ...ATURAN_KODE, message: t('structure.codeRule') }]}
          tooltip={t('structure.codeFixed')}
        >
          <Input maxLength={40} data-testid="isi-kode" />
        </Form.Item>
      ) : (
        /* ⛔ Kode TIDAK bisa diubah: ia dipakai di struk lama, laporan, dan berkas impor. Mengubahnya
           membuat data kemarin menunjuk ke sesuatu yang sudah bernama lain. */
        <Alert type="info" showIcon style={{ marginBottom: 16 }} title={t('structure.codeLocked')} />
      )}
      <Form.Item name="name" label={t('master.name')} rules={[{ required: true, min: 2 }]}>
        <Input maxLength={120} data-testid="isi-nama" />
      </Form.Item>
      <Form.Item name="sortOrder" label={t('structure.sortOrder')} initialValue={0}>
        <InputNumber min={0} style={{ width: '100%' }} data-testid="isi-urutan" />
      </Form.Item>
      {editing !== null && (
        <>
          <Form.Item name="isActive" label={t('outlets.status')} valuePropName="checked">
            <Switch
              data-testid="isi-aktif"
              checkedChildren={t('outlets.active')}
              unCheckedChildren={t('outlets.inactive')}
            />
          </Form.Item>
          {/* ⚠️ Beda yang paling sering tertukar, jadi disebut di tempat kejadiannya. */}
          <Alert type="warning" showIcon title={t('structure.activeVsOutlet')} />
        </>
      )}
    </>
  );

  return (
    <Space orientation="vertical" size="middle" style={{ width: '100%' }} data-testid="structure">
      <Space>
        <Typography.Text strong>{t('structure.brand')}</Typography.Text>
        <BrandPicker value={brandId} onChange={setBrandId} />
      </Space>
      <Alert type="info" showIcon title={t('structure.perBrand')} data-testid="structure-catatan" />

      {brandId === null ? (
        <Alert type="warning" showIcon title={t('structure.pickBrand')} data-testid="structure-pilih-brand" />
      ) : (
        <Tabs
          defaultActiveKey="dep"
          items={[
            {
              key: 'dep',
              label: <span data-testid="tab-dep">{t('master.departments')}</span>,
              children: (
                <CrudPanel<Department>
                  testId="dep"
                  title={t('master.departments')}
                  subtitle={t('structure.depSubtitle')}
                  canWrite={boleh}
                  newLabel={t('structure.newDepartment')}
                  columns={kolomDep}
                  fetchList={muatDep}
                  searchIn={(d) => `${d.code} ${d.name}`}
                  emptyText={t('structure.noDepartment')}
                  renderFields={(_f, editing) => fieldDasar(editing)}
                  toFormValues={(d) => ({
                    name: d.name, sortOrder: d.sort_order, isActive: d.is_active,
                  })}
                  onCreate={async (v) => {
                    await api.createDepartment({
                      brandId,
                      code: String(v['code']).toUpperCase(),
                      name: String(v['name']),
                      sortOrder: Number(v['sortOrder'] ?? 0),
                    });
                  }}
                  onUpdate={async (id, v, lama) => {
                    await api.updateMaster('departments', id, {
                      name: v['name'], sortOrder: Number(v['sortOrder'] ?? 0),
                    });
                    /* ⚠️ Status punya endpointnya sendiri — dan hanya disentuh bila memang berubah,
                       supaya jejak master tak dipenuhi peristiwa "diubah" yang tak mengubah apa pun. */
                    if (v['isActive'] !== lama.is_active) {
                      await api.setStatus('departments', id, v['isActive'] === true);
                    }
                  }}
                />
              ),
            },
            {
              key: 'kat',
              label: <span data-testid="tab-kat">{t('master.categories')}</span>,
              children: (
                <CrudPanel<Category>
                  testId="kat"
                  title={t('master.categories')}
                  subtitle={t('structure.katSubtitle')}
                  canWrite={boleh}
                  newLabel={t('structure.newCategory')}
                  columns={kolomKat}
                  fetchList={muatKat}
                  searchIn={(c) => `${c.code} ${c.name}`}
                  emptyText={t('structure.noCategory')}
                  /* ⛔⛔ Inilah hierarki yang MEMAKSA: tanpa departemen, tak ada kategori. */
                  blockedReason={department.length === 0 ? t('structure.needDepartment') : null}
                  renderFields={(_f, editing) => (
                    <>
                      <Form.Item
                        name="departmentId"
                        label={t('master.departments')}
                        rules={[{ required: true, message: t('master.pickDepartment') }]}
                      >
                        <Select
                          data-testid="isi-dep"
                          options={department.map((d) => ({
                            value: d.id, label: `${d.code} · ${d.name}`,
                          }))}
                        />
                      </Form.Item>
                      {fieldDasar(editing)}
                    </>
                  )}
                  toFormValues={(c) => ({
                    departmentId: c.department_id, name: c.name,
                    sortOrder: c.sort_order, isActive: c.is_active,
                  })}
                  onCreate={async (v) => {
                    await api.createCategory({
                      departmentId: String(v['departmentId']),
                      code: String(v['code']).toUpperCase(),
                      name: String(v['name']),
                      sortOrder: Number(v['sortOrder'] ?? 0),
                    });
                  }}
                  onUpdate={async (id, v, lama) => {
                    await api.updateMaster('categories', id, {
                      name: v['name'],
                      departmentId: v['departmentId'],
                      sortOrder: Number(v['sortOrder'] ?? 0),
                    });
                    if (v['isActive'] !== lama.is_active) {
                      await api.setStatus('categories', id, v['isActive'] === true);
                    }
                  }}
                />
              ),
            },
          ]}
        />
      )}
    </Space>
  );
}
